import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Newsletter from "@/components/Newsletter";
import { SEO } from "@/components/SEO";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [tags, setTags] = useState([]);
  const [byCat, setByCat] = useState({});
  const [cats, setCats] = useState([]);

  useEffect(() => {
    (async () => {
      const [f, l, v, a, tg, c] = await Promise.all([
        api.get("/articles", { params: { featured: true, limit: 5 } }),
        api.get("/articles", { params: { limit: 8 } }),
        api.get("/articles", { params: { media_type: "video", limit: 4 } }),
        api.get("/articles", { params: { media_type: "audio", limit: 3 } }),
        api.get("/articles/tags/popular"),
        api.get("/categories"),
      ]);
      setFeatured(f.data.items || []);
      setLatest(l.data.items || []);
      setVideos(v.data.items || []);
      setAudios(a.data.items || []);
      setTags(tg.data || []);
      setCats(c.data || []);

      const catMap = {};
      await Promise.all(
        (c.data || []).slice(0, 4).map(async (cat) => {
          const { data } = await api.get("/articles", { params: { category: cat.slug, limit: 5 } });
          catMap[cat.slug] = data.items || [];
        })
      );
      setByCat(catMap);
    })();
  }, []);

  const hero = featured[0];
  const heroSide = featured.slice(1, 5);

  return (
    <>
      <SEO
        title="ITTV News · Independent Reporting from Julia Fosah"
        description="ITTV News is an independent editorial publication covering world affairs, politics, technology and culture — from the desk of journalist Julia Fosah."
        image={hero?.cover_image}
        type="website"
      />

      {/* HERO */}
      <section className="container-editorial py-8 md:py-12" data-testid="hero-section">
        {hero ? (
          <div className="grid md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-8 reveal">
              <Link to={`/article/${hero.slug}`} className="block group">
                <div className="relative overflow-hidden img-zoom aspect-[16/10]">
                  <img src={hero.cover_image} alt={hero.title} className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 bg-[color:var(--accent)] text-white kicker px-3 py-1.5" data-testid="hero-badge">
                    Cover Story
                  </div>
                </div>
                <div className="mt-6">
                  <div className="kicker text-[color:var(--accent)] mb-3">{hero.category_name}</div>
                  <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight group-hover:text-[color:var(--accent)] transition-colors" data-testid="hero-title">
                    {hero.title}
                  </h1>
                  <p className="mt-5 text-lg md:text-xl text-[color:var(--text-muted)] max-w-3xl leading-relaxed">
                    {hero.excerpt}
                  </p>
                  <div className="mt-4 text-sm text-[color:var(--text-muted)]">
                    By <span className="text-[color:var(--text)] font-semibold">{hero.author_name}</span> · {new Date(hero.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} · {hero.read_time} min read
                  </div>
                </div>
              </Link>
            </div>
            <aside className="md:col-span-4 flex flex-col divide-y" style={{ borderColor: "hsl(var(--border-hsl))" }}>
              <div className="pb-4">
                <div className="kicker text-[color:var(--text-muted)] mb-2">Also on the front page</div>
              </div>
              {heroSide.map((a) => (
                <ArticleCard key={a.slug} article={a} size="sm" />
              ))}
            </aside>
          </div>
        ) : (
          <div className="text-center py-24 text-[color:var(--text-muted)]">Loading front page…</div>
        )}
      </section>

      {/* CATEGORY BANDS */}
      {Object.keys(byCat).length > 0 && (
        <div className="bg-[color:var(--surface)] border-y" style={{ borderColor: "hsl(var(--border-hsl))" }}>
          <div className="container-editorial py-16 space-y-16">
            {cats.slice(0, 4).map((cat) => {
              const items = byCat[cat.slug] || [];
              if (!items.length) return null;
              const [lead, ...rest] = items;
              return (
                <section key={cat.slug} data-testid={`section-${cat.slug}`}>
                  <div className="flex items-end justify-between mb-8 border-b pb-4" style={{ borderColor: "hsl(var(--border-hsl))" }}>
                    <div>
                      <div className="kicker text-[color:var(--accent)] mb-2">Section</div>
                      <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">{cat.name}</h2>
                    </div>
                    <Link to={`/category/${cat.slug}`} className="kicker hover-underline flex items-center gap-2" data-testid={`more-${cat.slug}`}>
                      All in {cat.name} <ArrowRight size={14} />
                    </Link>
                  </div>
                  <div className="grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-7">
                      <ArticleCard article={lead} size="lg" />
                    </div>
                    <div className="md:col-span-5 space-y-0">
                      {rest.slice(0, 4).map((a) => (
                        <ArticleCard key={a.slug} article={a} size="sm" />
                      ))}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      )}

      {/* VIDEO STRIP */}
      {videos.length > 0 && (
        <section className="container-editorial py-16" data-testid="video-strip">
          <div className="flex items-end justify-between mb-8 border-b pb-4" style={{ borderColor: "hsl(var(--border-hsl))" }}>
            <div>
              <div className="kicker text-[color:var(--accent)] mb-2">On screen</div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold tracking-tight">Video Reports</h2>
            </div>
            <Link to="/videos" className="kicker hover-underline flex items-center gap-2">All video & audio <ArrowRight size={14} /></Link>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {videos.map((v) => <ArticleCard key={v.slug} article={v} />)}
          </div>
        </section>
      )}

      {/* AUDIO / TAGS */}
      <section className="bg-[color:var(--surface)] border-y" style={{ borderColor: "hsl(var(--border-hsl))" }}>
        <div className="container-editorial py-16 grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2" data-testid="audio-section">
            <div className="kicker text-[color:var(--accent)] mb-2">In your ears</div>
            <h2 className="font-serif text-4xl font-bold tracking-tight mb-8">Audio Essays</h2>
            <div className="space-y-6">
              {audios.map((a) => (
                <div key={a.slug} className="grid md:grid-cols-3 gap-4 border p-4" style={{ borderColor: "hsl(var(--border-hsl))" }}>
                  <Link to={`/article/${a.slug}`} className="img-zoom overflow-hidden aspect-video md:aspect-square">
                    <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
                  </Link>
                  <div className="md:col-span-2">
                    <Link to={`/article/${a.slug}`} className="font-serif text-xl font-bold leading-tight hover:text-[color:var(--accent)]">{a.title}</Link>
                    <p className="text-sm text-[color:var(--text-muted)] mt-2 line-clamp-2">{a.excerpt}</p>
                    {a.media_url && (
                      <audio controls className="mt-3 w-full" data-testid={`audio-${a.slug}`}>
                        <source src={a.media_url} />
                      </audio>
                    )}
                  </div>
                </div>
              ))}
              {!audios.length && <p className="text-[color:var(--text-muted)]">Coming soon.</p>}
            </div>
          </div>
          <aside data-testid="tags-sidebar">
            <div className="kicker text-[color:var(--accent)] mb-2">Popular topics</div>
            <h2 className="font-serif text-3xl font-bold tracking-tight mb-6">Threads readers follow</h2>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <Link
                  key={t.tag}
                  to={`/search?q=${encodeURIComponent(t.tag)}`}
                  className="border px-3 py-1.5 kicker hover:bg-[color:var(--text)] hover:text-[color:var(--bg)] transition-colors"
                  style={{ borderColor: "hsl(var(--border-hsl))" }}
                  data-testid={`tag-${t.tag}`}
                >
                  {t.tag} <span className="opacity-40 ml-1">{t.count}</span>
                </Link>
              ))}
            </div>
            <div className="mt-10 border-t pt-8" style={{ borderColor: "hsl(var(--border-hsl))" }}>
              <div className="kicker text-[color:var(--accent)] mb-2">Latest</div>
              <div className="space-y-0">
                {latest.slice(0, 5).map((a) => <ArticleCard key={a.slug} article={a} size="sm" />)}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Newsletter />
    </>
  );
}
